import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { 
  History, 
  Sparkles, 
  Bug, 
  Shield, 
  Zap,
  ChevronDown,
  ChevronUp,
  Calendar,
  Clock,
  FileText,
  ArrowLeft
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { useNavigate } from 'react-router-dom';
import { 
  APP_VERSION, 
  VERSION_HISTORY, 
  formatVersionDate, 
  getChangeTypeColor, 
  getChangeTypeLabel 
} from '../../config/version';

const ChangelogPage = () => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [expandedVersions, setExpandedVersions] = useState([APP_VERSION.version]);

  const isAgency = ['agency', 'project_manager', 'customer_service'].includes(user?.role);

  if (!isAgency) {
    return null;
  }

  const toggleVersion = (version) => {
    setExpandedVersions(prev => 
      prev.includes(version) 
        ? prev.filter(v => v !== version)
        : [...prev, version]
    );
  };

  const getChangeIcon = (type) => {
    switch (type) {
      case 'feature':
        return <Sparkles className="w-4 h-4" />;
      case 'fix':
        return <Bug className="w-4 h-4" />;
      case 'security':
        return <Shield className="w-4 h-4" />;
      case 'improvement':
        return <Zap className="w-4 h-4" />;
      default:
        return <FileText className="w-4 h-4" />;
    }
  };

  // Count changes by type for current version
  const currentChangeCounts = APP_VERSION.changes.reduce((acc, change) => {
    acc[change.type] = (acc[change.type] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />
      
      <div className={cn("transition-all duration-300", collapsed ? "md:ml-20" : "md:ml-64")}>
        <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />
        
        <main className="p-4 md:p-8 pt-40 md:pt-36">
          {/* Header */}
          <div className="flex items-center gap-4 mb-8">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate(-1)}
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold" style={{ color: theme.colors?.primary }}>
                Manifeste des versions
              </h1>
              <p className="text-slate-500 mt-1">
                Historique complet des mises à jour du MSC Manager
              </p>
            </div>
          </div>

          {/* Current Version Summary */}
          <Card className="mb-8 border-2" style={{ borderColor: theme.colors?.accent }}>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-3">
                  <div 
                    className="w-12 h-12 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: theme.colors?.accent + '20' }}
                  >
                    <History className="w-6 h-6" style={{ color: theme.colors?.accent }} />
                  </div>
                  <div>
                    <span className="text-xl">Version actuelle</span>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge 
                        className="text-lg font-bold px-3 py-1"
                        style={{ backgroundColor: theme.colors?.accent, color: theme.colors?.primary }}
                      >
                        v{APP_VERSION.version}
                      </Badge>
                    </div>
                  </div>
                </CardTitle>
                <div className="text-right text-sm text-slate-500">
                  <div className="flex items-center gap-1 justify-end">
                    <Calendar className="w-4 h-4" />
                    <span>{formatVersionDate(APP_VERSION.buildDate)}</span>
                  </div>
                  <div className="text-xs mt-1">Fuseau horaire: Europe/Paris</div>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-3 mt-2">
                {currentChangeCounts.feature && (
                  <div className="flex items-center gap-2 bg-emerald-50 text-emerald-700 px-3 py-1.5 rounded-full">
                    <Sparkles className="w-4 h-4" />
                    <span className="font-medium">{currentChangeCounts.feature} nouveauté(s)</span>
                  </div>
                )}
                {currentChangeCounts.fix && (
                  <div className="flex items-center gap-2 bg-amber-50 text-amber-700 px-3 py-1.5 rounded-full">
                    <Bug className="w-4 h-4" />
                    <span className="font-medium">{currentChangeCounts.fix} correction(s)</span>
                  </div>
                )}
                {currentChangeCounts.security && (
                  <div className="flex items-center gap-2 bg-red-50 text-red-700 px-3 py-1.5 rounded-full">
                    <Shield className="w-4 h-4" />
                    <span className="font-medium">{currentChangeCounts.security} sécurité</span>
                  </div>
                )}
                {currentChangeCounts.improvement && (
                  <div className="flex items-center gap-2 bg-blue-50 text-blue-700 px-3 py-1.5 rounded-full">
                    <Zap className="w-4 h-4" />
                    <span className="font-medium">{currentChangeCounts.improvement} amélioration(s)</span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Version History */}
          <div className="space-y-4">
            {VERSION_HISTORY.map((release, index) => (
              <Card 
                key={release.version}
                className={cn(
                  "overflow-hidden transition-all",
                  index === 0 && "ring-2 ring-offset-2",
                )}
                style={index === 0 ? { ringColor: theme.colors?.accent } : {}}
              >
                <button
                  className="w-full p-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
                  onClick={() => toggleVersion(release.version)}
                >
                  <div className="flex items-center gap-4">
                    <Badge 
                      variant={index === 0 ? "default" : "secondary"}
                      className={cn(
                        "text-base font-mono px-3 py-1",
                        index === 0 && "text-white"
                      )}
                      style={index === 0 ? { backgroundColor: theme.colors?.primary } : {}}
                    >
                      v{release.version}
                    </Badge>
                    <div className="text-left">
                      <div className="flex items-center gap-2 text-sm text-slate-600">
                        <Clock className="w-4 h-4" />
                        {formatVersionDate(release.date)}
                      </div>
                      <div className="text-xs text-slate-400 mt-0.5">
                        {release.changes.length} modification(s)
                      </div>
                    </div>
                  </div>
                  {expandedVersions.includes(release.version) ? (
                    <ChevronUp className="w-5 h-5 text-slate-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-slate-400" />
                  )}
                </button>
                
                {expandedVersions.includes(release.version) && (
                  <div className="px-4 pb-4 border-t border-slate-100">
                    <ul className="space-y-2 mt-4">
                      {release.changes.map((change, changeIndex) => (
                        <li 
                          key={changeIndex}
                          className="flex items-start gap-3 p-2 rounded-lg hover:bg-slate-50"
                        >
                          <Badge 
                            variant="secondary"
                            className={cn(
                              "flex items-center gap-1 shrink-0 mt-0.5",
                              getChangeTypeColor(change.type)
                            )}
                          >
                            {getChangeIcon(change.type)}
                            {getChangeTypeLabel(change.type)}
                          </Badge>
                          <span className="text-slate-700">{change.description}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </Card>
            ))}
          </div>

          {/* Footer */}
          <div className="mt-8 text-center text-sm text-slate-400">
            <p>MSC Manager - Développé par Agence MSC</p>
            <p className="mt-1">Toutes les heures sont affichées en heure de Paris (Europe/Paris)</p>
          </div>
        </main>
      </div>
    </div>
  );
};

export default ChangelogPage;
